import { MonetizationType } from '@/@types/graphql-types';
import {
	GetGeoSwiperOffersDocument,
	GetGeoSwiperOffersQuery,
	GetGeoSwiperOffersQueryVariables,
} from '@/features/buybox/graphql/queries/GetGeoSwiperOffers.query';
import { getUserGeoLocation } from '@/helpers/geo-location-helper';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { CatalogueItem, freeTrialSwiperRules, getAllRawCatalogue } from '@/helpers/promotion-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import Provider from '@/interfaces/provider';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMounted } from '@vueuse/core';
import { MaybeRefOrGetter, toValue } from 'vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

type GeoSwiperOffers = AssertByTypename<GetGeoSwiperOffersQuery['node'], 'Movie' | 'Season' | 'Show'>;

export function useGeoSwiper({
	title,
	enable: enableQuery = false,
	/** offerShortName - param is required if fetching localised offer for the promotion.
	 * Used to determine if title has offer in geoLocation.
	 * Important for geoLocations where the provider is not an active provider in webLocale - e.g. amp v prv */
	offerShortName,
	offerCountry,
}: {
	title: MaybeRefOrGetter<TitleDetail | undefined>;
	enable?: MaybeRefOrGetter<boolean>;
	offerShortName?: MaybeRefOrGetter<string | undefined | null>;
	offerCountry?: MaybeRefOrGetter<string | null | undefined>;
}) {
	const { country, language } = storeToRefs(useLanguageStore());
	const { allProvidersByShortName } = storeToRefs(useConstantStore());
	const isMounted = useMounted();

	const isGeoSwiperCountry = computed(() => freeTrialSwiperRules[country.value] != null);

	const geoLocation = computed(() => {
		if (!isMounted.value) return null;

		return (getUserGeoLocation() ?? [])[0];
	});

	const countryOrGeoCountry = computed(() => toValue(offerCountry) ?? country.value);

	const _title = computed(() => toValue(title));
	const shortName = computed(() => toValue(offerShortName));
	const enabled = computed(() => toValue(enableQuery) && !!shortName && !!_title);

	const packages = computed(() => (!!shortName.value ? [shortName.value] : []));

	const { result, onError } = useQuery<GetGeoSwiperOffersQuery, GetGeoSwiperOffersQueryVariables>(
		GetGeoSwiperOffersDocument,
		() => ({
			country: countryOrGeoCountry.value,
			language: language.value,
			filter: {
				bestOnly: true,
				packages: packages.value,
				monetizationTypes: [MonetizationType.Flatrate, MonetizationType.Free] as any,
			},
			nodeId: _title.value?.id || '',
		}),
		{ enabled, fetchPolicy: 'no-cache' }
	);

	onError(error => {
		captureMessageForSentry('[Graphql GetGeoSwiperOffersQuery]', {
			where: '[useGeoSwiper.ts]',
			error,
		});
	});

	const geoSwiperOffers = computed(() => {
		if (!result.value?.node) return [];
		const node = result.value?.node as GeoSwiperOffers;

		return node.geoOffers;
	});

	function geoSwiperOfferFilter(
		item: CatalogueItem,
		isLocalisedPromotion: MaybeRefOrGetter<boolean>,
		hardcodedOfferShortName?: MaybeRefOrGetter<string | null>
	) {
		return toValue(isLocalisedPromotion) && item.shortName === toValue(hardcodedOfferShortName);
	}

	function geoSwiperPromotionCatalogue(
		offerCountry: MaybeRefOrGetter<string>,
		isLocalisedPromotion: MaybeRefOrGetter<boolean>
	) {
		if (!_title.value) return new Map<string, CatalogueItem>();

		return new Map<string, CatalogueItem>(
			getAllRawCatalogue({ country: toValue(offerCountry), title: _title.value })
				.filter(
					item =>
						!!allProvidersByShortName.value[item.shortName as keyof Provider] ||
						toValue(isLocalisedPromotion)
				)
				.map(item => [item.shortName, item])
		);
	}

	return {
		geoSwiperPromotionCatalogue,
		isGeoSwiperCountry,
		geoSwiperOfferFilter,
		geoSwiperOffers,
		geoLocation,
	};
}
